import React, { useState, useEffect } from 'react';
import moment from 'moment';
import PeopleLayout from '../layout/PeopleLayout';
import { Icon } from '../../common/components/Utilities';
import { UserTimesheet } from '../../projects/components/Timesheets';

function CompleteTimesheet(props) {
  const content = (
    <CompleteTimesheetView
      projectData={props.project_data}
      logTime={props.log_time}
      totalPercentage={props.total_percentage}
      totalMinutes={props.total_minutes}
      totalRemain={props.total_remain}
      start={props.start}
      end={props.end}
      user={props.user}
      csrfToken={props.csrfToken}
    />
  );

  return <PeopleLayout {...props} content={content} />;
}

function CompleteTimesheetView({
  csrfToken,
  user,
  start,
  end,
  projectData,
  logTime,
  totalPercentage,
  totalMinutes,
  totalRemain,
}) {
  const [projectState, setProjectState] = useState();
  const [total, setTotal] = useState();

  useEffect(() => {
    const newState = {};
    let newTotal = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const i in projectData) {
      if (projectData[i]) {
        newState[projectData[i].project.id] = projectData[i].percentage;
        newTotal += projectData[i].percentage;
      }
    }

    setProjectState(newState);
    setTotal(newTotal);
  }, [projectData]);

  const onChangeProgress = (projectId, value) => {
    const newState = { ...projectState };
    newState[projectId] = parseInt(value, 10);
    const newTotal = Object.keys(projectState)
      .filter((key) => parseInt(key, 10) !== projectId)
      .reduce((carry, id) => carry + projectState[id], parseInt(value, 10));

    setProjectState(newState);
    setTotal(newTotal);
  };

  const minutesToHuman = (minutes) => {
    const roundMinutes = Math.round(minutes);
    return `${Math.floor(roundMinutes / 60)}h ${roundMinutes % 60}m`;
  };

  if (logTime) {
    return (
      <>
        <div className="alert alert-danger">
          <Icon icon="exclamation-triangle" />
          {' '}
          <strong>
            Before you can complete your timesheet,
            you need to log more time in Google Calendar or Jira.
          </strong>
          <br />
          You logged
          {' '}
          {minutesToHuman(totalMinutes)}
          {' '}
          (
          {totalPercentage}
          % of your working hours) between
          {' '}
          {moment(start).format('D MMMM')}
          {' '}
          and
          {' '}
          {moment(end).format('D MMMM')}
          .
          You can use the timesheet below to find any gaps.
        </div>

        <UserTimesheet start={start} end={end} user={user} />
      </>
    );
  }

  if (totalPercentage >= 90) {
    return (
      <>
        <div className="alert alert-success">
          <Icon icon="check-circle" />
          {' '}
          <strong>You have already logged enough time!</strong>
          {' '}
          You can use the timesheet below to see the time you logged.
        </div>

        <UserTimesheet start={start} end={end} user={user} />
      </>
    );
  }

  return (
    <form className="form" action="/complete-timesheet" method="POST">
      <input type="hidden" name="_token" value={csrfToken} />
      <input type="hidden" name="start" value={start} />
      <input type="hidden" name="end" value={end} />

      <div className="mb-4 alert alert-warning">
        <strong>
          You logged
          {' '}
          {minutesToHuman(totalMinutes)}
          {' '}
          (
          {totalPercentage}
          % of your working hours) between
          {' '}
          {moment(start).format('D MMMM')}
          {' '}
          and
          {' '}
          {moment(end).format('D MMMM')}
          .
        </strong>
        <br />
        This tool will help you to allocate the remaining
        {' '}
        {minutesToHuman(totalRemain)}
        {' '}
        to the projects you have been working on.
        The suggested allocations below are based on the time you have already logged.
      </div>

      <TimeSplit
        projectData={projectData}
        projectState={projectState}
        onChangeProgress={onChangeProgress}
        total={total}
        minutesToHuman={minutesToHuman}
        totalRemain={totalRemain}
      />

      <div>
        <button type="submit" className="btn btn-success">
          <Icon icon="check mr-2" />
          {' '}
          Complete Timesheet
        </button>
      </div>
    </form>
  );
}

function TimeSplit({
  projectData, projectState, onChangeProgress, total, minutesToHuman, totalRemain,
}) {
  if (!projectState) {
    return '';
  }

  let alert = <div className="mb-4 mx-2 alert alert-info">When you complete your timesheet, extra time will be logged against these projects based on the split you have selected below.</div>;

  if (total < 100) {
    alert = (
      <div className="mb-4 mx-2 alert alert-warning">
        You have only allocated
        {' '}
        {total}
        % of your time.
      </div>
    );
  }

  if (total > 100) {
    alert = (
      <div className="mb-4 mx-2 alert alert-danger">
        You have allocated
        {' '}
        {total}
        % of your time.
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        How do you want to split your remaining time?
      </div>

      {alert}

      <table className="table">
        <tbody>
          {projectData.map((project) => (
            <tr key={project.project.id}>
              <th>{project.project.title}</th>
              <td>
                <div className="mb-" style={{ fontSize: '1.5em' }}>
                  {projectState[project.project.id]}
                  %
                </div>
                <div className="text-info">
                  {minutesToHuman((totalRemain / 100) * projectState[project.project.id])}
                </div>
              </td>
              <td width="70%" style={{ verticalAlign: 'middle' }}>
                <div className="slider">
                  <input
                    type="range"
                    className="custom-range"
                    name={`percentage[${project.project.id}]`}
                    value={projectState[project.project.id]}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(event) => {
                      onChangeProgress(project.project.id, event.target.value);
                    }}
                  />
                </div>

              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CompleteTimesheet;
